// git commit: "fix(AdminDashboard): correct API paths with restored adminApi base URL"
import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Users,
  Zap,
  Wallet,
  TrendingUp,
  RefreshCw,
  ArrowUpRight,
} from "lucide-react";
import adminApi from "../../api/adminApi";
import Currency from "../../components/Currency";

const STAT_CONFIG = [
  {
    key: "totalUsers",
    label: "Total Users",
    icon: Users,
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-50 dark:bg-blue-900/20",
    format: (v) => Number(v).toLocaleString(),
  },
  {
    key: "totalActivations",
    label: "Total Activations",
    icon: Zap,
    color: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-50 dark:bg-purple-900/20",
    format: (v) => Number(v).toLocaleString(),
  },
  {
    key: "pendingActivations",
    label: "Pending Now",
    icon: RefreshCw,
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-900/20",
    format: (v) => Number(v).toLocaleString(),
  },
  {
    key: "totalUsersBalance",
    label: "Users Total Balance",
    icon: Wallet,
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-50 dark:bg-green-900/20",
    isCurrency: true,
  },
  {
    key: "revenueToday",
    label: "Revenue Today",
    icon: TrendingUp,
    color: "text-purple-600 dark:text-purple-400",
    bg: "bg-purple-50 dark:bg-purple-900/20",
    isCurrency: true,
  },
  {
    key: "revenueWeek",
    label: "This Week",
    icon: TrendingUp,
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-50 dark:bg-blue-900/20",
    isCurrency: true,
  },
  {
    key: "revenueMonth",
    label: "This Month",
    icon: TrendingUp,
    color: "text-teal-600 dark:text-teal-400",
    bg: "bg-teal-50 dark:bg-teal-900/20",
    isCurrency: true,
  },
  {
    key: "totalRevenue",
    label: "All-Time Revenue",
    icon: ArrowUpRight,
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-50 dark:bg-green-900/20",
    isCurrency: true,
    highlight: true,
  },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalActivations: 0,
    pendingActivations: 0,
    totalUsersBalance: 0,
    revenueToday: 0,
    revenueWeek: 0,
    revenueMonth: 0,
    totalRevenue: 0,
    revenueChart: [],
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = async () => {
    try {
      // FIXED: adminApi base is /api/admin so paths are /dashboard and /wallets
      const [dashRes, walletsRes] = await Promise.all([
        adminApi.get("/dashboard"),
        adminApi.get("/wallets"),
      ]);

      const wallets = walletsRes.data || [];
      const totalUsersBalance = wallets.reduce(
        (sum, w) => sum + (w.balance || 0),
        0
      );

      setStats({ ...dashRes.data, totalUsersBalance });
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded-lg w-48 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="h-28 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {lastUpdated
              ? `Updated ${lastUpdated.toLocaleTimeString("en-NG", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}`
              : "Loading..."}
          </p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CONFIG.map(
          ({ key, label, icon: Icon, color, bg, format, isCurrency, highlight }) => (
            <div
              key={key}
              className={`bg-white dark:bg-gray-900 rounded-xl border p-4 transition-all hover:shadow-sm ${
                highlight
                  ? "border-purple-200 dark:border-purple-800"
                  : "border-gray-200 dark:border-gray-800"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}
                >
                  <Icon size={16} className={color} />
                </div>
                {highlight && (
                  <span className="text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded-full">
                    All time
                  </span>
                )}
              </div>
              <div className="mt-1">
                {isCurrency ? (
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    <Currency amount={stats[key] || 0} />
                  </p>
                ) : (
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {format ? format(stats[key] || 0) : stats[key] || 0}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {label}
                </p>
              </div>
            </div>
          )
        )}
      </div>

      {/* Revenue chart */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Revenue Trend
            </h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Last 30 days
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Revenue
            </span>
          </div>
        </div>

        {stats.revenueChart?.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={stats.revenueChart}
                margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  className="dark:stroke-gray-800"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                  formatter={(value) => [
                    `₦${Number(value).toLocaleString()}`,
                    "Revenue",
                  ]}
                  labelStyle={{ color: "#6b7280", marginBottom: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#7c3aed"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: "#7c3aed", strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <TrendingUp
                size={32}
                className="mx-auto text-gray-300 dark:text-gray-600 mb-2"
              />
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No revenue data yet
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}